import Cookies from "js-cookie";
import { Method, SimpleResource } from "rest-hooks";
import { navigate } from "@reach/router";

class NetworkError extends Error {
	status: number;
	response: Response;

	constructor(response: Response) {
		super(response.statusText);
		this.status = response.status;
		this.response = response;
	}
}

abstract class TokenResource extends SimpleResource {
	/** A function to mutate all request options for fetch */
	static fetchOptionsPlugin = (options: RequestInit) => {
		const token = Cookies.get("X-RequestVerificationToken") ?? "";
		return {
			...options,
			headers: {
				...options.headers,
				"X-RequestVerificationToken": token,
			},
		};
	};

	static fetchResponse(
		method: Method,
		url: string,
		body?: Readonly<object | string>
	) {
		let options: RequestInit = {
			method: method.toUpperCase(),
			headers: {
				"Content-Type": "application/json",
			},
		};
		if (this.fetchOptionsPlugin) options = this.fetchOptionsPlugin(options);
		if (body) options.body = JSON.stringify(body);
		return fetch(url, options)
			.then((response) => {
				if (!response.ok) {
					if (response.status === 401) {
						navigate("/logout", { state: { errorCode: 401 } });
					}
					throw new NetworkError(response);
				}
				return response;
			})
			.catch((error) => {
				// ensure CORS, network down, and parse errors are still caught by NetworkErrorBoundary
				if (error instanceof TypeError) {
					(error as any).status = 400;
				}
				throw error;
			});
	}

	/** Perform network request and resolve with json body */
	static fetch(method: Method, url: string, body?: Readonly<object | string>) {
		return this.fetchResponse(method, url, body).then((response: Response) => {
			if (
				!response.headers.get("content-type")?.includes("json") ||
				response.status === 204
			)
				return response.text();
			return response.json().catch((error) => {
				error.status = 400;
				throw error;
			});
		});
	}
}

export default TokenResource;
